import authService from './authService';
import {
  StockMovement,
  StockMovementFormData,
  StockMovementSearchModel,
} from '../types/stockMovement';
import { PaginatedResponse, ApiResponse } from '../types/common';

// Shape returned by the backend (camelCase) before normalization
interface RawStockMovement {
  stockMovementId: number;
  movementDate: string;
  originType: number;
  originReference?: string;
  purchaseOrderId?: number;
  purchaseOrderNumber?: number;
  orderId?: number;
  orderNumber?: string;
  materialReceiptItemId?: number;
  materialReceiptId?: number;
  productId?: number;
  rawMaterialId?: number;
  movementType: number;
  quantity: number;
  previousBalance?: number;
  newBalance?: number;
  purchasePrice?: number;
  costPrice?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  lastUpdatedBy: string;
  productDescription?: string;
  productSku?: string;
  rawMaterialDescription?: string;
  rawMaterialCode?: string;
}

class StockMovementService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private normalize(raw: RawStockMovement): StockMovement {
    // Workaround: backend may return incorrect originType.
    // Infer the correct type from the populated FK fields.
    let originType = raw.originType;
    if (raw.orderId != null) {
      originType = 1; // PedidoVenda
    } else if (raw.purchaseOrderId != null && raw.materialReceiptItemId == null) {
      originType = 0; // PedidoCompra
    }

    return {
      id: raw.stockMovementId,
      movement_date: raw.movementDate,
      origin_type: originType,
      origin_reference: raw.originReference,
      purchase_order_id: raw.purchaseOrderId,
      purchase_order_number: raw.purchaseOrderNumber,
      order_id: raw.orderId,
      order_number: raw.orderNumber,
      material_receipt_item_id: raw.materialReceiptItemId,
      material_receipt_id: raw.materialReceiptId,
      product_id: raw.productId,
      raw_material_id: raw.rawMaterialId,
      movement_type: raw.movementType,
      quantity: raw.quantity,
      previous_balance: raw.previousBalance,
      new_balance: raw.newBalance,
      purchase_price: raw.purchasePrice,
      cost_price: raw.costPrice,
      notes: raw.notes,
      created_at: raw.createdAt,
      updated_at: raw.updatedAt,
      created_by: raw.createdBy,
      last_updated_by: raw.lastUpdatedBy,
      product_description: raw.productDescription,
      product_sku: raw.productSku,
      raw_material_description: raw.rawMaterialDescription,
      raw_material_code: raw.rawMaterialCode,
    };
  }

  /**
   * Lista lançamentos de estoque de um produto
   */
  async getMovementsByProduct(
    productId: number,
    searchModel: StockMovementSearchModel
  ): Promise<PaginatedResponse<StockMovement>> {
    const params = this.buildParams(searchModel);
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/stock-movements/product/${productId}?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar lançamentos de estoque do produto');
    const result: PaginatedResponse<RawStockMovement> = await response.json();
    return { ...result, data: result.data.map((m) => this.normalize(m)) };
  }

  /**
   * Lista lançamentos de estoque de uma matéria-prima
   */
  async getMovementsByRawMaterial(
    rawMaterialId: number,
    searchModel: StockMovementSearchModel
  ): Promise<PaginatedResponse<StockMovement>> {
    const params = this.buildParams(searchModel);
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/stock-movements/raw-material/${rawMaterialId}?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar lançamentos de estoque da matéria-prima');
    const result: PaginatedResponse<RawStockMovement> = await response.json();
    return { ...result, data: result.data.map((m) => this.normalize(m)) };
  }

  /**
   * Cria um lançamento de estoque manual (AcertoManual ou Balanco)
   */
  async createMovement(data: StockMovementFormData): Promise<StockMovement> {
    const apiClient = await this.getApiClient();
    // Round numeric values to avoid IEEE 754 floating-point precision errors
    const roundTo = (value: number, decimals: number): number => {
      const factor = Math.pow(10, decimals);
      return Math.round(value * factor) / factor;
    };
    // Backend expects camelCase field names
    const payload: Record<string, unknown> = {
      originType: data.origin_type,
      movementType: data.movement_type,
      quantity: roundTo(data.quantity, 4),
      notes: data.notes,
      productId: data.product_id ?? null,
      rawMaterialId: data.raw_material_id ?? null,
    };
    if (data.purchase_price !== undefined) payload.purchasePrice = roundTo(data.purchase_price, 2);
    if (data.cost_price !== undefined) payload.costPrice = roundTo(data.cost_price, 2);
    const response = await apiClient.post('/stock-movements', payload);
    if (!response.ok) throw new Error('Falha ao criar lançamento de estoque');
    const result: ApiResponse<RawStockMovement> = await response.json();
    return this.normalize(result.data!);
  }

  /**
   * Obtém o último preço de compra de um produto
   */
  async getLastPurchasePriceByProduct(productId: number): Promise<number | null> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/stock-movements/product/${productId}/last-purchase-price`);
    if (!response.ok) throw new Error('Falha ao buscar último preço de compra do produto');
    const result: ApiResponse<{ lastPurchasePrice: number | null }> = await response.json();
    return result.data?.lastPurchasePrice ?? null;
  }

  /**
   * Obtém o último preço de compra de uma matéria-prima
   */
  async getLastPurchasePriceByRawMaterial(rawMaterialId: number): Promise<number | null> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/stock-movements/raw-material/${rawMaterialId}/last-purchase-price`);
    if (!response.ok) throw new Error('Falha ao buscar último preço de compra da matéria-prima');
    const result: ApiResponse<{ lastPurchasePrice: number | null }> = await response.json();
    return result.data?.lastPurchasePrice ?? null;
  }

  /**
   * Obtém um lançamento de estoque por ID
   */
  async getMovementById(id: number): Promise<StockMovement> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/stock-movements/${id}`);
    if (!response.ok) throw new Error('Falha ao buscar lançamento de estoque');
    const result: ApiResponse<RawStockMovement> = await response.json();
    return this.normalize(result.data!);
  }

  private buildParams(searchModel: StockMovementSearchModel): URLSearchParams {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
    });
    if (searchModel.originType !== undefined) params.append('origin_type', searchModel.originType.toString());
    if (searchModel.movementType !== undefined) params.append('movement_type', searchModel.movementType.toString());
    if (searchModel.startDate) params.append('start_date', searchModel.startDate);
    if (searchModel.endDate) params.append('end_date', searchModel.endDate);
    return params;
  }
}

export const stockMovementService = new StockMovementService();
export default stockMovementService;
